import React, { useEffect, useState } from 'react';
import { Box, Typography, Chip } from '@mui/material';
import { Link as ChainIcon } from '@mui/icons-material';

const ChainPreviewOverlay = ({
  chainPreview,
  currentHoverDate,
  calendarData,
  addWorkingDays,
  getISODateString,
}) => {
  const [previewPositions, setPreviewPositions] = useState([]);

  useEffect(() => {
    if (!chainPreview || !currentHoverDate) {
      setPreviewPositions([]);
      return;
    }

    const { draggedProcess, allProcesses, offsets } = chainPreview;

    // Calculate where each process will land
    const positions = allProcesses.map((proc) => {
      const offset = offsets.get(proc.id + (proc.instanceId || '')) || 0;
      const baseOffset =
        offsets.get(draggedProcess.id + (draggedProcess.instanceId || '')) || 0;
      const relativeOffset = offset - baseOffset;

      const targetDate = addWorkingDays(
        new Date(currentHoverDate),
        relativeOffset,
        []
      );

      return {
        process: proc,
        targetDate,
        dateKey: getISODateString(targetDate),
        relativeOffset,
      };
    });

    setPreviewPositions(positions);
  }, [chainPreview, currentHoverDate]);

  if (!chainPreview || !currentHoverDate || previewPositions.length === 0) {
    return null;
  }

  return (
    <>
      {/* Backdrop Overlay */}
      <Box
        className="chainPreviewOverlay"
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          bgcolor: 'rgba(0, 0, 0, 0.1)',
          backdropFilter: 'blur(2px)',
          zIndex: 9999,
          pointerEvents: 'none',
        }}
      />

      {/* Chain Preview Info Banner */}
      <Box
        sx={{
          position: 'fixed',
          top: 80,
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 10001,
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
          px: 3,
          py: 1.5,
          borderRadius: 4,
          boxShadow: 3,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          pointerEvents: 'none',
        }}
      >
        <ChainIcon />
        <Typography variant="body2" fontWeight={600}>
          Kette wird verschoben: {previewPositions.length} Prozesse
        </Typography>
        <Chip
          label={`Chain ID: ${chainPreview.chainId.substring(0, 8)}...`}
          size="small"
          sx={{
            bgcolor: 'rgba(255, 255, 255, 0.2)',
            color: 'inherit',
          }}
        />
      </Box>

      {/* Ghost Previews on Calendar Cells */}
      {previewPositions.map(({ process, dateKey, relativeOffset }) => {
        // Find the calendar cell for this date
        const cellElement = document.querySelector(
          `[data-date-key="${dateKey}"]`
        );

        if (!cellElement) return null;

        const rect = cellElement.getBoundingClientRect();
        const isDraggedProcess = process.id === chainPreview.draggedProcess.id;

        return (
          <Box
            key={`${process.id}-${dateKey}`}
            className="chainPreviewGhost"
            sx={{
              position: 'fixed',
              top: rect.top + 50,
              left: rect.left + 10,
              width: rect.width - 20,
              zIndex: 10000,
              bgcolor: isDraggedProcess
                ? 'primary.light'
                : 'primary.container',
              border: 2,
              borderStyle: 'dashed',
              borderColor: isDraggedProcess ? 'primary.main' : 'primary.light',
              borderRadius: 1.5,
              p: 1.5,
              opacity: isDraggedProcess ? 0.9 : 0.7,
              pointerEvents: 'none',
              animation: 'ghostFloat 1.5s ease-in-out infinite',
              '@keyframes ghostFloat': {
                '0%, 100%': {
                  transform: 'translateY(0)',
                },
                '50%': {
                  transform: 'translateY(-4px)',
                },
              },
              boxShadow: 2,
            }}
          >
            {/* Process Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <ChainIcon
                sx={{
                  fontSize: 16,
                  color: isDraggedProcess ? 'primary.main' : 'text.secondary',
                }}
              />
              <Typography
                variant="caption"
                fontWeight={700}
                sx={{
                  color: isDraggedProcess ? 'primary.main' : 'text.primary',
                }}
              >
                {process.anr || process.label || 'Prozess'}
              </Typography>
              {isDraggedProcess && (
                <Chip
                  label="AKTIV"
                  size="small"
                  color="primary"
                  sx={{ height: 16, fontSize: '0.65rem', fontWeight: 600 }}
                />
              )}
            </Box>

            {/* Process Details */}
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                color: 'text.secondary',
                fontSize: '0.7rem',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {process.auftragsbezeichnung || 'Keine Beschreibung'}
            </Typography>

            {/* Position Indicator */}
            <Box
              sx={{
                mt: 0.5,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  fontSize: '0.65rem',
                  color: 'text.disabled',
                  fontStyle: 'italic',
                }}
              >
                Offset: {relativeOffset > 0 ? '+' : ''}
                {relativeOffset} {Math.abs(relativeOffset) === 1 ? 'Tag' : 'Tage'}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  fontSize: '0.65rem',
                  fontWeight: 600,
                  color: 'success.main',
                }}
              >
                {process.hours || 0}h
              </Typography>
            </Box>
          </Box>
        );
      })}
    </>
  );
};

export default ChainPreviewOverlay;
